import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { X, Plus, Trash2, Search, PlayCircle, Info, CheckCircle2 } from "lucide-react";

export default function QuickWorkoutModal({ open, onClose, onSaved }) {
    const [clients, setClients] = useState([]);
    const [clientId, setClientId] = useState("");
    const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [title, setTitle] = useState("");
    const [exercises, setExercises] = useState([]);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [library, setLibrary] = useState([]);
    const [q, setQ] = useState("");
    const [expanded, setExpanded] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!open) return;
        setErr(""); setDone(false);
        api.get("/clients").then(({ data }) => {
            setClients(data);
            if (data[0] && !clientId) setClientId(data[0].id);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!pickerOpen) return;
        api.get("/exercises", { params: q ? { q } : {} }).then(({ data }) => setLibrary(data));
    }, [pickerOpen, q]);

    if (!open) return null;

    const addExercise = (ex) => {
        setExercises([...exercises, {
            exercise_id: ex.id,
            name: ex.name,
            sets: 3,
            reps: "8-12",
            rest: "60s",
            notes: "",
            video_url: ex.video_url || "",
        }]);
        setPickerOpen(false);
    };
    const addCustom = () => setExercises([...exercises, { name: "Custom exercise", sets: 3, reps: "10", rest: "60s", notes: "" }]);
    const update = (i, patch) => { const a = [...exercises]; a[i] = { ...a[i], ...patch }; setExercises(a); };
    const remove = (i) => setExercises(exercises.filter((_, ix) => ix !== i));

    const save = async () => {
        if (!clientId || exercises.length === 0) { setErr("Pick a client and add at least one exercise."); return; }
        setBusy(true); setErr("");
        try {
            const { data } = await api.post("/workouts", {
                client_id: clientId,
                date,
                title: title || `Workout · ${new Date(date).toLocaleDateString()}`,
                type: "workout",
                exercises: exercises.map((e) => ({
                    exercise_id: e.exercise_id || null,
                    name: e.name,
                    sets: Number(e.sets) || 3,
                    reps: e.reps || "",
                    rest: e.rest || "",
                    notes: e.notes || "",
                })),
                notes: "",
                status: "scheduled",
            });
            setDone(true);
            if (onSaved) onSaved(data);
            setTimeout(() => { onClose(); setDone(false); setExercises([]); setTitle(""); }, 1200);
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="quick-workout-modal">
            <div className="bg-[var(--surface)] border border-[var(--border-bright)] w-full max-w-3xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-5 border-b border-[var(--border)]">
                    <div>
                        <p className="label-tiny mb-1">/ Straight to it</p>
                        <h3 className="font-serif text-3xl">Build a workout</h3>
                    </div>
                    <button onClick={onClose} aria-label="Close"><X size={18} /></button>
                </div>
                <div className="flex-1 overflow-auto p-5 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                            <label className="label-tiny block mb-2">Client</label>
                            <select className="input-dark" value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="quick-wk-client">
                                <option value="">Pick client…</option>
                                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="label-tiny block mb-2">Date</label>
                            <input type="date" className="input-dark" value={date} onChange={(e) => setDate(e.target.value)} data-testid="quick-wk-date" />
                        </div>
                        <div>
                            <label className="label-tiny block mb-2">Title (optional)</label>
                            <input className="input-dark" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Lower body power" />
                        </div>
                    </div>

                    <div className="space-y-2">
                        {exercises.map((e, i) => (
                            <div key={i} className="border border-[var(--border)] bg-[var(--bg-deep)] p-3" data-testid={`quick-wk-ex-${i}`}>
                                <div className="flex items-center gap-2 mb-2">
                                    <input className="bg-transparent border-b border-transparent hover:border-[var(--border)] focus:border-[var(--gold)] outline-none font-medium flex-1" value={e.name} onChange={(ev) => update(i, { name: ev.target.value })} />
                                    {e.video_url && (
                                        <a href={e.video_url} target="_blank" rel="noreferrer" className="text-[var(--gold)] hover:text-[var(--gold-bright)]" title="Demo">
                                            <PlayCircle size={14} />
                                        </a>
                                    )}
                                    <button onClick={() => remove(i)} className="text-[var(--text-muted)] hover:text-[var(--danger)]"><Trash2 size={13} /></button>
                                </div>
                                <div className="grid grid-cols-4 gap-2 text-xs">
                                    <div><p className="label-tiny mb-1">Sets</p><input type="number" min="1" className="input-dark py-1.5 text-sm text-center" value={e.sets} onChange={(ev) => update(i, { sets: parseInt(ev.target.value) || 1 })} /></div>
                                    <div><p className="label-tiny mb-1">Reps</p><input className="input-dark py-1.5 text-sm" value={e.reps} onChange={(ev) => update(i, { reps: ev.target.value })} placeholder="8-12" /></div>
                                    <div><p className="label-tiny mb-1">Rest</p><input className="input-dark py-1.5 text-sm" value={e.rest} onChange={(ev) => update(i, { rest: ev.target.value })} placeholder="60s" /></div>
                                    <div><p className="label-tiny mb-1">Notes</p><input className="input-dark py-1.5 text-sm" value={e.notes} onChange={(ev) => update(i, { notes: ev.target.value })} placeholder="Tempo 3-0-1" /></div>
                                </div>
                            </div>
                        ))}
                        <div className="flex gap-2">
                            <button onClick={() => setPickerOpen(true)} className="btn-ghost flex-1 inline-flex items-center justify-center gap-2" data-testid="quick-wk-add-from-library"><Plus size={14} /> From library</button>
                            <button onClick={addCustom} className="btn-ghost flex-1 inline-flex items-center justify-center gap-2" data-testid="quick-wk-add-custom"><Plus size={14} /> Custom</button>
                        </div>
                    </div>
                </div>
                <div className="border-t border-[var(--border)] p-4 flex items-center justify-between gap-3">
                    {err && <span className="text-sm text-[var(--danger)]" data-testid="quick-wk-error">{err}</span>}
                    {done && <span className="text-[var(--gold-bright)] inline-flex items-center gap-2 text-sm"><CheckCircle2 size={14} /> Workout saved</span>}
                    <div className="flex gap-2 ml-auto">
                        <button onClick={onClose} className="btn-ghost">Cancel</button>
                        <button onClick={save} disabled={busy} className="btn-primary" data-testid="quick-wk-save">
                            {busy ? "Saving…" : "Save workout"}
                        </button>
                    </div>
                </div>
            </div>

            {/* Library picker */}
            {pickerOpen && (
                <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4" onClick={() => setPickerOpen(false)} data-testid="quick-wk-picker">
                    <div className="bg-[var(--surface)] border border-[var(--border-bright)] w-full max-w-xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between p-4 border-b border-[var(--border)]">
                            <h4 className="font-serif text-2xl">Pick an exercise</h4>
                            <button onClick={() => setPickerOpen(false)}><X size={18} /></button>
                        </div>
                        <div className="p-3 border-b border-[var(--border)] relative">
                            <Search size={14} className="absolute left-6 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                            <input className="input-dark pl-9" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} autoFocus data-testid="quick-wk-picker-search" />
                        </div>
                        <div className="flex-1 overflow-auto">
                            {library.map((ex) => (
                                <div key={ex.id} className="border-b border-[var(--border)]">
                                    <div className="px-4 py-3 flex items-center gap-2">
                                        <button onClick={() => addExercise(ex)} className="text-left flex-1 min-w-0" data-testid={`quick-wk-pick-${ex.id}`}>
                                            <p className="font-medium truncate">{ex.name}</p>
                                            <p className="text-xs text-[var(--text-muted)] font-mono">{ex.category} · {ex.primary_muscle || "—"}</p>
                                        </button>
                                        <button onClick={() => setExpanded(expanded === ex.id ? null : ex.id)} className="text-[var(--text-muted)] hover:text-[var(--gold)]" title="Details"><Info size={13} /></button>
                                        {ex.video_url && <a href={ex.video_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-[var(--gold)]" title="Demo"><PlayCircle size={14} /></a>}
                                        <button onClick={() => addExercise(ex)} className="text-[var(--gold)] font-mono">+</button>
                                    </div>
                                    {expanded === ex.id && <div className="px-4 pb-3 text-xs text-[var(--text-muted)] border-l-2 border-[var(--gold-deep)] ml-4">{ex.description || "—"}</div>}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
